import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Modal } from '@sorare/core/src/atoms/Modal';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BrandXXS, HeadlineXL } from '@sorare/core/src/atoms/typography';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isTypeFn } from '@sorare/core/src/lib/gql';
import { qualityNames } from '@sorare/core/src/lib/players';

import { useClaimRewards } from 'hooks/so5/useClaimRewards';

import { RewardBase } from '../RewardBase';
import { CardReveal } from './CardReveal';
import type { CardReward_so5Reward } from './__generated__/index.graphql';
import icon from './assets/icon.svg';

const Root = styled.div`
  isolation: isolate;
  position: relative;
  transform-style: preserve-3d;
  aspect-ratio: 210 / 340;
`;
const CardImage = styled.img`
  object-fit: cover;
  height: 100%;
`;
const Foreground = styled.div`
  width: 100%;
  height: 100%;

  transform: translateZ(-1px);

  .revealed & {
    transform: rotateY(180deg) translateZ(0px);
    opacity: 0;
  }
  transition:
    0.4s transform ease-in-out,
    0.4s opacity ease-in-out;
`;
const Background = styled.div`
  position: absolute;
  height: 100%;
  transform: rotateY(180deg) translateZ(1px);
  opacity: 0;

  .revealed & {
    transform: rotateY(360deg) translateZ(1px);
    opacity: 1;
  }
  transition:
    0.4s transform ease-in-out,
    0.4s opacity ease-in-out;
`;

enum CardRevealSteps {
  IDLE,
  REVEALING,
  REVEALED,
}
type Props = { reward: CardReward_so5Reward; disableClaim?: boolean };
export const CardReward = ({ reward, disableClaim }: Props) => {
  const { sport } = useSportContext();
  const [step, setStep] = useState(CardRevealSteps.IDLE);
  const [claim] = useClaimRewards();
  const { id, so5Leaderboard, rewards } = reward;
  const cardReward = rewards.find(isTypeFn('AnyCardReward'));
  const rewardedCard = cardReward?.card;
  const uppercasedQuality = cardReward?.quality?.toUpperCase() || '';
  const qualityName =
    uppercasedQuality in qualityNames
      ? qualityNames[uppercasedQuality as keyof typeof qualityNames]
      : '';
  const qualitySplitted = qualityName.split(' ');

  const onClaim = async () => {
    await claim({ so5RewardIds: [id] });
    setStep(CardRevealSteps.REVEALING);
  };

  return (
    <Root
      className={classNames({ revealed: step === CardRevealSteps.REVEALED })}
    >
      {step === CardRevealSteps.REVEALING && !!rewardedCard && (
        <Modal>
          <CardReveal
            card={rewardedCard}
            onAnimationEnd={() => setStep(CardRevealSteps.REVEALED)}
          />
        </Modal>
      )}
      <Background>
        {!!rewardedCard?.pictureUrl && (
          <CardImage src={rewardedCard.pictureUrl} alt="" />
        )}
      </Background>
      <Foreground>
        <RewardBase
          icon={<img src={icon} alt="" />}
          body={
            <Horizontal>
              {qualitySplitted.length === 1 ? (
                <HeadlineXL>{qualitySplitted.at(0)}</HeadlineXL>
              ) : (
                <>
                  <BrandXXS color="var(--c-nd-600)">
                    <FormattedMessage {...glossary.tier} />
                  </BrandXXS>
                  <HeadlineXL>{qualitySplitted.at(1)}</HeadlineXL>
                </>
              )}
            </Horizontal>
          }
          rarity={rewardedCard?.rarityTyped}
          leaguePictureUrl={
            sport === Sport.FOOTBALL
              ? so5Leaderboard.so5League.headerUrl
              : undefined
          }
          onReveal={!disableClaim ? onClaim : undefined}
          revealed={step === CardRevealSteps.REVEALED}
        />
      </Foreground>
    </Root>
  );
};

CardReward.fragments = {
  so5Reward: gql`
    fragment CardReward_so5Reward on So5Reward {
      slug
      id
      so5Leaderboard {
        slug
        so5League {
          slug
          headerUrl
        }
      }
      rewards {
        id
        ... on AnyCardReward {
          id
          quality
          card {
            slug
            pictureUrl
            rarityTyped
            ...CardReveal_anyCard
          }
        }
      }
    }
    ${CardReveal.fragments.anyCard}
  ` as TypedDocumentNode<CardReward_so5Reward>,
};
